import {
  faCalendarDays,
  faInfoCircle,
  faPersonRunning,
  faPlus,
  faUpload,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { AdvancedSelect, Button, Modal } from '@orobix/orobix-fe-components'
import React, { useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'

import { FullscreenModal } from '../../components/FullscreenModal/FullscreenModal'
import { OptimizationProcess } from '../../types'
import {
  Dataset,
  DATASET_DONE,
  getOptimizationTypeStr,
  OPTIMIZATION_ORDERS,
  OPTIMIZATION_RESOURCES,
  OptimizationType,
} from '../../types/dataset'
import { get, post } from '../../utils/api'
import { formatDateTime } from '../../utils/date'
import { useStyles } from './OptimizationProcessesActionsButton.styles'
import { OptimizationProcessesSelectionTable } from './OptimizationProcessesSelectionTable'

// Custom Tooltip Component
const CustomTooltip = ({ children }: { children: React.ReactNode }) => {
  const [isVisible, setIsVisible] = useState(false)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()
  const classes = useStyles()

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node)
      ) {
        setIsVisible(false)
      }
    }

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isVisible])

  return (
    <div className={classes.tooltipWrapper} ref={tooltipRef}>
      <div
        role="button"
        tabIndex={0}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onClick={() => setIsVisible(!isVisible)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            setIsVisible(!isVisible)
          }
        }}
      >
        {children}
      </div>
      {isVisible && (
        <div className={classes.tooltipContent}>
          <div className={classes.tooltipText}>
            {t('Zip archive tooltip text', {
              sheetName: t('START_END_PROJECT sheet name'),
            })}
          </div>
          <div className={classes.tooltipImageWrapper}>
            <img
              src="/sample_curves.png"
              alt={t('Sample curves example')}
              className={classes.tooltipImage}
            />
          </div>
          <div className={classes.tooltipArrow} />
        </div>
      )}
    </div>
  )
}

export const OptimizationProcessesActionsButton = () => {
  const { t } = useTranslation()
  const classes = useStyles()
  const [modal, setModal] = useState(false)
  const [listModal, setListModal] = useState(false)
  const [selectedProcessId, setSelectedProcessId] = useState<number | null>(
    null
  )
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [selectedXlsxFile, setSelectedXlsxFile] = useState<File | null>(null)
  const [optimizationType, setOptimizationType] =
    useState<OptimizationType>(OPTIMIZATION_ORDERS)
  const [processName, setProcessName] = useState<string>('')
  const [processNotes, setProcessNotes] = useState<string>('')

  // Get the latest dataset
  const { data: latestDataset } = useQuery<Dataset>(
    ['data-ingestions', 'latest'],
    () => get<Dataset>('/data-ingestions/latest/'),
    {
      retry: false,
    }
  )

  const handleCloseModal = () => {
    setOptimizationType(OPTIMIZATION_ORDERS) // Reset to default value
    setSelectedProcessId(null)
    setSelectedFile(null)
    setSelectedXlsxFile(null)
    setProcessName('')
    setProcessNotes('')
    setModal(false)
  }

  const handleSelectProcess = (processId: number) => {
    setSelectedProcessId(processId)
    setSelectedFile(null) // Clear selected file when a process is selected
    setListModal(false)
    setModal(true)
  }

  const handleOptimizationConfirm = () => {
    if (
      optimizationType === OPTIMIZATION_ORDERS ||
      optimizationType === OPTIMIZATION_RESOURCES
    ) {
      // Open the list dialog for Orders and Resources type
      setModal(false)
      setListModal(true)
    }
  }

  const handleManualUpload = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.zip'
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (file) {
        setSelectedFile(file)
        setSelectedProcessId(null) // Clear selected process when file is chosen
      }
    }
    input.click()
  }

  const handleXlsxUpload = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.xlsx'
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (file) {
        setSelectedXlsxFile(file)
      }
    }
    input.click()
  }

  const { mutate: createOptimizationProcess, isLoading: isCreating } =
    useCreateOptimizationProcess(
      handleCloseModal,
      latestDataset?.id ? String(latestDataset.id) : '',
      optimizationType,
      selectedProcessId,
      selectedFile,
      selectedXlsxFile,
      processName,
      processNotes
    )

  const getSelectionDescription = () => {
    if (optimizationType === OPTIMIZATION_ORDERS) {
      if (selectedFile) {
        return (
          <Trans
            i18nKey="Run a new optimization process based on uploaded zip file <name>{{ fileName }}</name>?"
            values={{ fileName: selectedFile.name }}
            components={{ name: <strong /> }}
          />
        )
      }
      if (selectedProcessId) {
        return (
          <Trans
            i18nKey="Run a new optimization process based on existing optimization process of id <id>{{ processId }}</id>?"
            values={{ processId: selectedProcessId }}
            components={{ id: <strong /> }}
          />
        )
      }
    }
    if (optimizationType === OPTIMIZATION_RESOURCES) {
      if (selectedProcessId) {
        return (
          <Trans
            i18nKey="Selected optimization process of type 'Orders' with id: <id>{{ processId }}</id>"
            values={{ processId: selectedProcessId }}
            components={{ id: <strong /> }}
          />
        )
      }
    }
    return null
  }

  const renderOrdersDefaultDescription = () => (
    <>
      <p>{t('Choose a way to run optimization process:')}</p>
      <ul>
        <li>
          {t(
            "selecting an existing optimization process clicking on 'Existing opt.' button"
          )}
        </li>
        <li>
          {t("uploading new data by clicking on 'New data from zip' button.")}
          <CustomTooltip>
            <FontAwesomeIcon
              icon={faInfoCircle}
              className={classes.tooltipIcon}
            />
          </CustomTooltip>
        </li>
      </ul>
    </>
  )

  const renderResourcesDefaultDescription = () => (
    <p>
      {t(
        "Select an existing optimization process by clicking on 'Existing Opt.' button"
      )}
    </p>
  )

  const renderFooter = () => {
    if (optimizationType === OPTIMIZATION_ORDERS) {
      return (
        <>
          <div className={classes.buttonContainer}>
            <Button
              icon={<FontAwesomeIcon icon={faCalendarDays} />}
              label={t('Existing Opt.')}
              type="action"
              onClick={handleOptimizationConfirm}
              disabled={isCreating}
            />
            <Button
              icon={<FontAwesomeIcon icon={faUpload} />}
              label={t('New data from zip')}
              type="action"
              onClick={handleManualUpload}
              disabled={isCreating}
            />
          </div>
          {getSelectionDescription() && (
            <div className={classes.selectionContainer}>
              <p className={classes.selectionDescription}>
                {getSelectionDescription()}
              </p>
              <div className={classes.centeredButtonWrapper}>
                <Button
                  icon={<FontAwesomeIcon icon={faPersonRunning} />}
                  label={t('Run')}
                  type="action"
                  onClick={() => {
                    if (latestDataset?.id) {
                      createOptimizationProcess()
                    }
                  }}
                  loading={isCreating}
                  disabled={isCreating || !latestDataset?.id}
                />
              </div>
            </div>
          )}
        </>
      )
    }

    if (optimizationType === OPTIMIZATION_RESOURCES) {
      return (
        <>
          <div className={classes.centeredRow}>
            <Button
              icon={<FontAwesomeIcon icon={faCalendarDays} />}
              label={t('Existing Opt.')}
              type="action"
              onClick={handleOptimizationConfirm}
              disabled={isCreating}
            />
          </div>
          {selectedProcessId && (
            <div className={classes.selectionContainer}>
              <p className={classes.selectionDescription}>
                {getSelectionDescription()}
              </p>
              <div className={classes.centeredButtonWrapper}>
                <Button
                  icon={<FontAwesomeIcon icon={faUpload} />}
                  label={t('Upload resource availability from xlsx')}
                  type="action"
                  onClick={handleXlsxUpload}
                  disabled={isCreating}
                />
              </div>
              {selectedXlsxFile && (
                <>
                  <p className={classes.selectionDescription}>
                    <Trans
                      i18nKey="Selected resource file: <fileName>{{ fileName }}</fileName>"
                      values={{ fileName: selectedXlsxFile.name }}
                      components={{ fileName: <strong /> }}
                    />
                  </p>
                  <p className={classes.selectionDescription}>
                    {t(
                      "Press the 'Run' button to start an optimization process based on the selections made"
                    )}
                  </p>
                  <div className={classes.centeredButtonWrapper}>
                    <Button
                      icon={<FontAwesomeIcon icon={faPersonRunning} />}
                      label={t('Run')}
                      type="action"
                      onClick={() => {
                        if (latestDataset?.id) {
                          createOptimizationProcess()
                        }
                      }}
                      loading={isCreating}
                      disabled={isCreating || !latestDataset?.id}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )
    }

    return null
  }

  const renderModalContent = () => {
    if (!latestDataset || latestDataset.status !== DATASET_DONE) {
      return <Trans i18nKey="No available data to run optimization process" />
    }

    // Always show the dropdown for selecting optimization type
    return (
      <>
        {latestDataset && (
          <div className={classes.datasetInfoBlock}>
            <Trans
              i18nKey="New optimization process will use last successfully data synced on <date>{{ syncDate }}</date> of id <id>{{ datasetId }}</id>"
              values={{
                syncDate: formatDateTime(latestDataset.createdAt || new Date()),
                datasetId: latestDataset.id,
              }}
              components={{
                date: <strong />,
                id: <strong />,
              }}
            />
          </div>
        )}
        <div className={classes.fieldWrapper}>
          <label htmlFor="processName">
            {t('Optimization process name (optional)')}
          </label>
          <input
            id="processName"
            type="text"
            maxLength={256}
            value={processName}
            onChange={(e) => setProcessName(e.target.value)}
            className={classes.textInput}
          />
        </div>
        <div className={classes.fieldWrapper}>
          <label htmlFor="processNotes">{t('Notes')}</label>
          <textarea
            id="processNotes"
            value={processNotes}
            onChange={(e) => setProcessNotes(e.target.value)}
            rows={3}
            className={classes.textarea}
          />
        </div>
        <p>{t('Select optimization type:')}</p>
        <AdvancedSelect<{ value: OptimizationType; label: string }>
          name="optimizationType"
          id="optimizationType"
          value={{
            value: optimizationType,
            label: getOptimizationTypeStr(optimizationType),
          }}
          onChange={(option) => {
            if (option) {
              setOptimizationType(option.value)
              setSelectedProcessId(null) // Clear selected process when type changes
              setSelectedFile(null) // Clear selected file when type changes
            }
          }}
          options={[
            {
              value: OPTIMIZATION_ORDERS,
              label: getOptimizationTypeStr(OPTIMIZATION_ORDERS),
            },
            {
              value: OPTIMIZATION_RESOURCES,
              label: getOptimizationTypeStr(OPTIMIZATION_RESOURCES),
            },
          ]}
        />
        {optimizationType === OPTIMIZATION_ORDERS &&
          renderOrdersDefaultDescription()}
        {optimizationType === OPTIMIZATION_RESOURCES &&
          renderResourcesDefaultDescription()}
      </>
    )
  }

  const handleOpenModal = () => {
    setOptimizationType(OPTIMIZATION_ORDERS) // Reset to default value
    setProcessName('')
    setProcessNotes('')
    setModal(true)
  }

  return (
    <>
      <Button
        icon={<FontAwesomeIcon icon={faPlus} />}
        onClick={handleOpenModal}
        type="action"
        label={t('New')}
        title={t('New optimization process')}
        size="md"
        loading={isCreating}
        disabled={isCreating}
      />

      {modal && (
        <Modal
          title={t('New optimization process')}
          onCloseRequest={handleCloseModal}
          footer={renderFooter()}
        >
          {renderModalContent()}
        </Modal>
      )}

      {listModal && (
        <FullscreenModal
          title="Select an optimization process"
          onCloseRequest={() => {
            setListModal(false)
            setOptimizationType(OPTIMIZATION_ORDERS)
          }}
        >
          <>
            <p className={classes.modalDescription}>
              {t(
                'Click to select a completed optimization process to be used for a new optimization process'
              )}
            </p>
            <OptimizationProcessesSelectionTable
              onSelectProcess={handleSelectProcess}
              filterByType={OPTIMIZATION_ORDERS}
            />
          </>
        </FullscreenModal>
      )}
    </>
  )
}

const useCreateOptimizationProcess = (
  setModal: (value: boolean) => void,
  datasetId: string,
  optimizationType: OptimizationType,
  referenceOptimizationProcessId?: number | null,
  fileInputs?: File | null,
  resourceAvailabilityFile?: File | null,
  name?: string,
  notes?: string
) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  return useMutation(
    ['optimization-processes', 'create'],
    () => {
      if (!datasetId || datasetId === '' || datasetId === 'undefined') {
        throw new Error('No valid dataset available')
      }

      const formData = new FormData()
      formData.append('data_ingestion_id', datasetId)
      formData.append('optimization_type', optimizationType)

      if (referenceOptimizationProcessId) {
        formData.append(
          'reference_optimization_process',
          String(referenceOptimizationProcessId)
        )
      }

      if (fileInputs) {
        formData.append('file_inputs', fileInputs)
      }

      if (resourceAvailabilityFile) {
        formData.append('resource_availability_file', resourceAvailabilityFile)
      }

      if (name) {
        formData.append('name', name)
      }

      if (notes) {
        formData.append('notes', notes)
      }

      // Debug logging
      // eslint-disable-next-line no-console
      console.log('Creating optimization process with:', {
        datasetId,
        optimizationType,
        referenceOptimizationProcessId,
        hasFileInputs: !!fileInputs,
        hasResourceFile: !!resourceAvailabilityFile,
      })

      return post<OptimizationProcess>(
        '/optimization-processes/',
        formData,
        undefined,
        true // payloadIsNotAnObject - skip snakeCaseKeys transformation
      )
    },
    {
      retry: false,
      onSuccess: () => {
        queryClient.invalidateQueries(['optimization-processes'])

        toast.success(
          t('A new optimization process has been added to the processing queue')
        )

        setModal(false)
      },
      onError: (error: unknown) => {
        let errorMessage = t(
          'An error occurred. Please try again later or contact the support.'
        )
        if (error && typeof error === 'object') {
          const err = error as {
            response?: {
              status?: number
              data?: {
                error?: string
                detail?: string
                message?: string
                non_field_errors?: string[]
                [key: string]: unknown
              }
            }
            message?: string
          }

          const { response, message } = err

          // For 5xx errors, show generic message (avoid HTML error pages)
          if (response?.status && response.status >= 500) {
            errorMessage = t(
              'A server error occurred. Please try again later or contact the support.'
            )
          } else if (response?.data) {
            // For 4xx errors, try to extract detailed error message
            const {
              error: backendError,
              detail,
              message: dataMessage,
              non_field_errors: nonFieldErrors,
              ...otherFields
            } = response.data
            // Check for common error message fields
            errorMessage =
              backendError ||
              detail ||
              dataMessage ||
              // Handle validation errors (array of messages)
              (Array.isArray(nonFieldErrors)
                ? nonFieldErrors.join(', ')
                : '') ||
              // Handle field-specific errors
              (Object.keys(otherFields).length > 0
                ? Object.entries(otherFields)
                    .filter(([key]) => key !== '__all__')
                    .map(([, value]) =>
                      Array.isArray(value) ? value.join(', ') : String(value)
                    )
                    .join('; ')
                : '') ||
              message ||
              errorMessage
          } else if (message) {
            errorMessage = message
          }
        }
        toast.error(errorMessage, { autoClose: false })
        setModal(false)
      },
    }
  )
}
